import { defaultMeta } from '@/constants/defaultMeta';
import { parseDateRangeSearch } from '@/lib/parseDateRange';
import { sortBy } from '@/lib/sortBy';
import { useQuery } from '@/react-query/hooks/useQuery/useQuery';
import { monthlyReportQueries } from '@/services/payment/affiliate/monthly-report.queries';
import { useSearch } from '@tanstack/react-router';

export const useGetMonthlyReport = () => {
  const { sort, q, status, createdDate, month, page, fetch } = useSearch({
    from: '/(app)/payment-management/affiliate-log/$monthly-report',
  });

  // `month` is the clicked month from the list; the date filter (`createdDate`) may only narrow it.
  const monthRange = month ? parseDateRangeSearch(month) : undefined;
  const selectedRange = parseDateRangeSearch(
    Array.isArray(createdDate) ? createdDate.join(',') : createdDate,
    monthRange,
  );
  const clampedRange = monthRange && {
    start: Math.max(selectedRange.start, monthRange.start),
    end: Math.min(selectedRange.end, monthRange.end),
  };
  // A range entirely outside the month (e.g. an edited URL) falls back to the whole month.
  const parsedDateRange = clampedRange
    ? clampedRange.start <= clampedRange.end
      ? clampedRange
      : monthRange
    : selectedRange;

  const { data, isFetched, isLoading } = useQuery({
    ...monthlyReportQueries.list({
      fromDate: parsedDateRange.start,
      toDate: parsedDateRange.end,
      search: q || undefined,
      status,
      row: page ?? 0,
      fetch: fetch ?? 10,
    }),
    select: (res) => ({
      ...res,
      // An empty result comes back as `data: null` ("Record not found").
      data: sortBy(res.data ?? [], sort, { createdDate: 'requestDate' }),
    }),
  });

  return {
    monthlyReportData: data?.data || [],
    isFetched,
    isLoading,
    pagination: data?.meta?.pagination ?? defaultMeta,
    monthBounds: monthRange ? { from: new Date(monthRange.start), to: new Date(monthRange.end) } : undefined,
  };
};

export default useGetMonthlyReport;
